// Empty-state card shown when the thread has no messages yet.
//
// Mirrors the kyoto prototype's welcome: a centred greeting plus three
// suggestion chips. Clicking a chip prefills the composer with a longer,
// more specific phrasing than the chip label and focuses the textarea so
// Loic can edit before sending — we deliberately do NOT auto-submit,
// because some chips ("Open Notepad and type hello") would otherwise hand
// control of the VM to the agent on a single click.
//
// Below the chips: a "Recent workflows" surface that fetches the caller's
// most-recently-run workflows from kyoto (`?sort=recent`) and surfaces
// them with one-click Run buttons. "See all" opens the full workflows
// library. This is the welcome-screen equivalent of the screenshot's
// "RECENT WORKFLOWS" section — no design fidelity to a specific count;
// we render up to RECENT_LIMIT rows.

import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import { Sparkles, ListChecks, ChevronRight, Play, Plus } from "lucide-react";
import { useComposerRuntime } from "@assistant-ui/react";
import { useCopilotController } from "../CopilotRuntimeProvider";
import { getRecentWorkflows, getWorkflow } from "../workflows/workflowsApi";

const SUGGESTIONS = [
  {
    label: "How do I open Task Manager?",
    prompt: "How do I open the Task Manager on this machine?",
  },
  {
    label: "Show me my Downloads",
    prompt: "Show me where my Downloads folder is",
  },
  {
    label: "Open Notepad and type hello",
    prompt: "Open Notepad and type 'Hello world'",
  },
];

const RECENT_LIMIT = 4;

export const ThreadWelcome = observer(function ThreadWelcome() {
  const composer = useComposerRuntime();
  const controller = useCopilotController();
  const [recent, setRecent] = useState(null); // null = loading, [] = none, [..] = list
  const [busyRunId, setBusyRunId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getRecentWorkflows(RECENT_LIMIT)
      .then((rows) => { if (!cancelled) setRecent(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setRecent([]); });
    return () => { cancelled = true; };
  }, []);

  const prefill = (prompt) => {
    composer.setText(prompt);
    // setText is synchronous on the runtime, but the input only re-reads
    // state on the next render. Defer the focus so the cursor lands at the
    // end of the freshly-mounted value instead of being clobbered by the
    // controlled-input update.
    requestAnimationFrame(() => {
      const ta = document.querySelector(
        '.copilot-root textarea[aria-label="Copilot message"]',
      );
      if (ta) {
        ta.focus();
        const end = ta.value.length;
        ta.setSelectionRange(end, end);
      }
    });
  };

  // Run-from-welcome path. Fetches the full workflow so we know whether it
  // has parameters; with-params surfaces the same WorkflowParamModal the
  // library uses (mounted in chatPanel.jsx, driven by
  // controller.workflowParamPrompt). No-params executes directly. We
  // deliberately skip the "Start workflow? Chat will be cleared" confirm
  // chatPanel raises for the library path — on the welcome screen there
  // are no messages to lose.
  const runWorkflow = async (id) => {
    if (controller.busy) return;
    setBusyRunId(id);
    try {
      const pb = await getWorkflow(id);
      const hasParams = (pb.parameters || []).length > 0;
      if (hasParams) {
        runInAction(() => {
          controller.workflowParamPrompt = {
            workflow: pb,
            paramValues: null,
            serverErrors: null,
          };
        });
        return;
      }
      await controller.executeWorkflow(pb.id, {}, { workflowName: pb.name });
    } catch (e) {
      window.alert("Couldn't start the workflow. Try again.");
    } finally {
      setBusyRunId(null);
    }
  };

  const seeAll = () => controller.openWorkflowsLibrary();

  return (
    <div className="copilot-welcome">
      <div className="copilot-welcome-hero">
        <span className="copilot-welcome-glyph" aria-hidden="true">
          <Sparkles className="copilot-welcome-glyph-icon" />
        </span>
        <h2 className="copilot-welcome-title">
          How can I help with what you're doing?
        </h2>
        <p className="copilot-welcome-subtitle">
          Ask me to <em>do</em> something — or to <em>show you</em> how.
          I'll always ask before taking control of your screen.
        </p>
      </div>

      <div className="copilot-welcome-suggestions">
        {SUGGESTIONS.map((s) => (
          <button
            key={s.label}
            type="button"
            onClick={() => prefill(s.prompt)}
            className="copilot-welcome-chip"
          >
            <span className="copilot-welcome-chip-label">{s.label}</span>
          </button>
        ))}
      </div>

      {recent && recent.length > 0 && (
        <div className="copilot-welcome-recent">
          <div className="copilot-welcome-recent-head">
            <span className="copilot-welcome-recent-title">Recent workflows</span>
            <button
              type="button"
              className="copilot-welcome-recent-link"
              onClick={seeAll}
            >
              See all <ChevronRight className="copilot-welcome-recent-link-icon" />
            </button>
          </div>
          {/* Plain <div> stack rather than <ul>/<li> because the copilot
              panel mounts inside .the-topbar, whose global rules force
              ul → inline-block and .list-horizontal li → inline-flex.
              See the "Reset .the-topbar list bleed" block in copilot.css. */}
          <div className="copilot-welcome-recent-list">
            {recent.map((w) => (
              <div key={w.id} className="copilot-welcome-recent-row">
                <span className="copilot-welcome-recent-row-icon" aria-hidden="true">
                  <ListChecks className="copilot-welcome-recent-row-icon-svg" />
                </span>
                <span className="copilot-welcome-recent-row-name" title={w.summary || w.name}>
                  {w.name}
                </span>
                <button
                  type="button"
                  className="copilot-welcome-recent-run"
                  onClick={() => runWorkflow(w.id)}
                  disabled={controller.busy || busyRunId === w.id}
                  aria-label={`Run ${w.name}`}
                >
                  <Play className="copilot-welcome-recent-run-icon" />
                  <span>Run</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {recent && recent.length === 0 && (
        <button
          type="button"
          className="copilot-welcome-create"
          onClick={() => controller.openWorkflowsLibrary()}
        >
          <Plus className="copilot-welcome-create-icon" aria-hidden="true" />
          <span>Create your first workflow</span>
        </button>
      )}
    </div>
  );
});
